import { Button } from "@/components/ui/button";
import { useDebounce } from "@uidotdev/usehooks";
import { AnimatePresence, motion } from "framer-motion";
import React, { useMemo, useState } from "react";

import { InboxGroupHeader } from "@/components/InboxGroupHeader/InboxGroupHeader";
import {
  IndexLayout,
  IndexLayoutHeader,
  IndexLayoutHeaderDescription,
  IndexLayoutHeaderIcon,
  IndexLayoutHeaderTitle,
  IndexLayoutSearch,
  IndexLayoutSearchBar,
  IndexLayoutStickyHeader,
  UnderlineTab,
  UnderlineTabs,
} from "@cohesive/ui-components";
import {
  Archive,
  Check,
  Inbox,
  MessagesSquare,
  Search,
  Star,
  Zap,
} from "lucide-react";

import { InboxCard, Spinner } from "@cohesive/ui-components";

// Import refactored utilities and hooks
import { useUpdateInboxItem } from "@/api/inbox";
import { useAppNavigate, usePaneLocation } from "@/components/PaneRouter";
import { ROUTES } from "@/constants/routes";
import { useInboxItemSelection } from "@/hooks/useInboxItemSelection";
import * as analytics from "@/utils/analytics";
import { route } from "@/utils/route";
import { InboxItem } from "@cohesive/api-client";
import { useHeaderInfo } from "./hooks/useHeaderInfo";
import { useInboxDataInfinite } from "./hooks/useInboxData";
import { useInboxFilters } from "./hooks/useInboxFilters";
import { FilterType } from "./types";

export const InboxPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );

  // Use custom hooks for clean separation of concerns
  const { activeFilter, setActiveFilter } = useInboxFilters();

  // Inbox interaction hooks
  const { mutate: updateInboxItem } = useUpdateInboxItem();
  const appNavigate = useAppNavigate();
  const rightPaneLocation = usePaneLocation("rightPane");

  const isSearchActive = debouncedSearchQuery.trim().length > 0;
  const isSearching = searchQuery !== debouncedSearchQuery;

  // Get inbox data with API-based filtering (with infinite scroll)
  const {
    displayItems,
    priorityItems,
    allItems,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    sentryRef,
    rootRef,
  } = useInboxDataInfinite({
    searchQuery: debouncedSearchQuery,
    activeFilter,
  });

  // Use selection hook
  const selection = useInboxItemSelection({ items: displayItems });

  // Helper to check if item is currently viewed
  const isCurrentlyViewed = (item: InboxItem): boolean => {
    if (!rightPaneLocation?.pathname) return false;

    const pathname = rightPaneLocation.pathname;
    const inboxItemMatch = pathname.match(/\/inbox\/([^\/]+)/);
    if (inboxItemMatch && inboxItemMatch[1] === item.id) return true;

    const sessionMatch = pathname.match(/\/sessions\/([^\/]+)/);
    if (sessionMatch && sessionMatch[1] === item.flow_session?.id) return true;

    return false;
  };

  // Event handlers
  const handleItemClick = (item: InboxItem) => {
    // Mark as read if unread
    if (!item.is_read) {
      updateInboxItem({
        id: item.id,
        data: { is_read: true },
      });
    }

    // Navigate based on item type
    if (item.flow_session?.id) {
      appNavigate({
        pane: "rightPane",
        to: route(ROUTES.SESSIONS.SESSION, {
          sessionId: item.flow_session.id,
        }),
        options: { state: { allowNewChat: true } },
      });
    }

    analytics.trackInboxItemClicked({ inboxItem: item });
  };

  const handleStarClick = (e: React.MouseEvent, item: InboxItem) => {
    e.stopPropagation();
    analytics.trackInboxItemStarred({ inboxItem: item });
    updateInboxItem({
      id: item.id,
      data: { is_starred: !item.is_starred },
    });
  };

  const handleArchiveClick = (e: React.MouseEvent, item: InboxItem) => {
    e.stopPropagation();
    analytics.trackInboxItemArchived({ inboxItem: item });
    updateInboxItem({
      id: item.id,
      data: { is_archived: !item.is_archived },
    });
  };

  const toggleGroup = (groupId: string) => {
    setCollapsedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Get dynamic header info
  const currentFilterInfo = useHeaderInfo(activeFilter);

  // Create tabs without dropdowns
  const underlineTabs: UnderlineTab[] = useMemo(
    () => [
      {
        id: FilterType.ALL,
        label: "All",
        icon: <Inbox className="h-4 w-4" />,
      },
      {
        id: FilterType.CHATS,
        label: "Chats",
        icon: <MessagesSquare className="h-4 w-4" />,
      },
      {
        id: FilterType.STARRED,
        label: "Starred",
        icon: <Star className="h-4 w-4" />,
      },
      {
        id: FilterType.ARCHIVED,
        label: "Archived",
        icon: <Archive className="h-4 w-4" />,
      },
    ],
    [],
  );

  const handleTabChange = (tabId: string) => {
    setActiveFilter(tabId as FilterType);
  };

  // Render function to choose the correct row component
  const renderInboxItem = (
    item: InboxItem,
    _index: number,
    _totalItems: number,
    className?: string,
  ) => {
    return (
      <InboxCard
        item={item}
        onClick={() => handleItemClick(item)}
        onStarClick={(e) => handleStarClick(e, item)}
        onArchiveClick={(e) => handleArchiveClick(e, item)}
        onSelectionChange={selection.handleSelectionChange}
        isSelected={selection.isItemSelected(item.id)}
        isCurrentlyViewed={isCurrentlyViewed(item)}
        className={className}
      />
    );
  };

  // Dynamic label for the "All" section based on active filter
  const getAllItemsLabel = (
    filter: FilterType,
  ): { key: string; label: string; icon: React.ReactNode } => {
    switch (filter) {
      case FilterType.CHATS:
        return {
          key: "chats",
          label: "All Chats",
          icon: <MessagesSquare className="w-4 h-4" />,
        };
      case FilterType.STARRED:
        return {
          key: "starred",
          label: "All Starred",
          icon: <Star className="w-4 h-4" />,
        };
      case FilterType.ARCHIVED:
        return {
          key: "archived",
          label: "All Archived",
          icon: <Archive className="w-4 h-4" />,
        };
      default:
        return {
          key: "all",
          label: "All Items",
          icon: <Inbox className="w-4 h-4" />,
        };
    }
  };

  return (
    <IndexLayout>
      <title>Inbox</title>
      <IndexLayoutStickyHeader>
        <IndexLayoutHeader>
          <IndexLayoutHeaderIcon>
            {
              currentFilterInfo.icon as React.ReactElement<{
                className?: string;
              }>
            }
          </IndexLayoutHeaderIcon>
          <IndexLayoutHeaderTitle>
            {currentFilterInfo.title}
          </IndexLayoutHeaderTitle>
          <IndexLayoutHeaderDescription>
            {currentFilterInfo.description}
          </IndexLayoutHeaderDescription>
        </IndexLayoutHeader>

        {/* Search Bar */}
        <IndexLayoutSearch>
          {selection.hasSelectedItems ? (
            <InboxGroupHeader
              groupId="selection"
              groupName="Selection"
              items={[]}
              icon={<Check className="w-4 h-4" />}
              selectedCount={selection.selectedCount}
              isCollapsed={false}
              onToggleGroup={() => {}}
              onToggleGroupSelection={() => {}}
              areAllGroupItemsSelected={() => false}
              onBulkStar={selection.handleBulkStar}
              onBulkArchive={selection.handleBulkArchive}
              onClearSelection={selection.handleClearSelection}
              selectedItemsStarred="none"
              selectedItemsArchived="none"
            />
          ) : (
            <IndexLayoutSearchBar
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery("")}
              placeholder="Search inbox..."
            />
          )}
        </IndexLayoutSearch>

        {/* Filter Tabs */}
        <div className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="pb-4">
            <UnderlineTabs
              tabs={underlineTabs}
              value={activeFilter}
              onValueChange={handleTabChange}
              className="@container"
            />
          </div>
        </div>
      </IndexLayoutStickyHeader>
      {/* Content */}
      {isLoading || isSearching ? (
        <div className="flex items-center justify-center h-full">
          <Spinner size="xl" />
        </div>
      ) : displayItems.length === 0 && isSearchActive ? (
        <div className="flex items-center justify-center h-full">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="text-center"
          >
            <div className="rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4 w-16 h-16">
              <Search className="text-muted-foreground h-8 w-8" />
            </div>
            <h3 className="font-medium mb-2 text-base">
              No matching items found
            </h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              We couldn't find any inbox items matching "{searchQuery}"
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSearchQuery("")}
              className="text-xs"
            >
              Clear search
            </Button>
          </motion.div>
        </div>
      ) : displayItems.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="text-center"
          >
            <div className="rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4 w-16 h-16">
              <Inbox className="text-muted-foreground h-8 w-8" />
            </div>
            <h3 className="font-medium mb-2 text-base">
              {activeFilter === FilterType.ALL
                ? "Your inbox is empty"
                : `No ${activeFilter}`}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {activeFilter === FilterType.ALL
                ? "You're all caught up! New items will appear here."
                : `No items match the ${activeFilter} filter`}
            </p>
            {activeFilter !== FilterType.ALL && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTabChange(FilterType.ALL)}
                className="text-xs"
              >
                View all items
              </Button>
            )}
          </motion.div>
        </div>
      ) : (
        <div className="bg-background" ref={rootRef}>
          <div className="space-y-4">
            {/* Priority Section */}
            {priorityItems.length > 0 && (
              <div className="space-y-2">
                <InboxGroupHeader
                  groupId="priority"
                  groupName="Priority"
                  items={priorityItems}
                  icon={<Zap className="w-4 h-4" />}
                  badgeCount={priorityItems.length}
                  isCollapsed={collapsedGroups.has("priority")}
                  onToggleGroup={toggleGroup}
                  onToggleGroupSelection={selection.toggleGroupSelection}
                  areAllGroupItemsSelected={selection.areAllGroupItemsSelected}
                  selectedCount={selection.selectedCount}
                  onBulkStar={selection.handleBulkStar}
                  onBulkArchive={selection.handleBulkArchive}
                  onClearSelection={selection.handleClearSelection}
                  selectedItemsStarred={selection.selectedItemsStarred}
                  selectedItemsArchived={selection.selectedItemsArchived}
                />

                <AnimatePresence initial={false}>
                  {!collapsedGroups.has("priority") && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-2 px-1">
                        {priorityItems.map((item, index) => (
                          <React.Fragment key={item.id}>
                            {renderInboxItem(item, index, priorityItems.length)}
                          </React.Fragment>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* All Items Section */}
            {allItems.length > 0 && (
              <div className="space-y-2">
                <InboxGroupHeader
                  groupId="all"
                  groupName={getAllItemsLabel(activeFilter).label}
                  items={allItems}
                  icon={getAllItemsLabel(activeFilter).icon}
                  badgeCount={0}
                  isCollapsed={collapsedGroups.has("all")}
                  onToggleGroup={toggleGroup}
                  onToggleGroupSelection={
                    priorityItems.length > 0
                      ? selection.toggleGroupSelection
                      : selection.toggleGroupSelection
                  }
                  areAllGroupItemsSelected={
                    priorityItems.length > 0
                      ? selection.areAllGroupItemsSelected
                      : selection.areAllGroupItemsSelected
                  }
                  selectedCount={
                    priorityItems.length > 0 ? 0 : selection.selectedCount
                  }
                  onBulkStar={
                    priorityItems.length > 0
                      ? undefined
                      : selection.handleBulkStar
                  }
                  onBulkArchive={
                    priorityItems.length > 0
                      ? undefined
                      : selection.handleBulkArchive
                  }
                  onClearSelection={
                    priorityItems.length > 0
                      ? undefined
                      : selection.handleClearSelection
                  }
                  selectedItemsStarred={
                    priorityItems.length > 0
                      ? "none"
                      : selection.selectedItemsStarred
                  }
                  selectedItemsArchived={
                    priorityItems.length > 0
                      ? "none"
                      : selection.selectedItemsArchived
                  }
                />

                <AnimatePresence initial={false}>
                  {!collapsedGroups.has("all") && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-2 px-1">
                        {allItems.map((item, index) => (
                          <React.Fragment key={item.id}>
                            {renderInboxItem(item, index, allItems.length)}
                          </React.Fragment>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Infinite scroll trigger */}
          {hasNextPage && (
            <div ref={sentryRef} className="flex justify-center py-4">
              {isFetchingNextPage && <Spinner size="lg" />}
            </div>
          )}
        </div>
      )}
    </IndexLayout>
  );
};
